import { DownloadOutlined } from '@ant-design/icons';
import { App, Button, Tooltip } from 'antd';
import React from 'react';
import BudgetDowload from 'PDF/Presupuesto/BudgetDowload/BudgetDowload';
import BudgetDowloadV2 from 'PDF/PresupuestoV2/BudgetDowload/BudgetDowload';
import BudgetDowloadV3 from 'PDF/PresupuestoV3/BudgetDowload/BudgetDowload';
import { deactivateBudgetApi } from 'api/budget';
import { createBudgetVersionApi } from 'api/hallazgoFlujo';
import { getAccessTokenApi } from 'api/auth';
import notificationApi from 'utils/notificationApi';
import { useNavigate } from 'react-router-dom';
import paths from 'assets/routes/paths';

/**
 * Icono de descarga del presupuesto (un espacio fijo de la fila). Mantiene el
 * mecanismo actual (abre una notificación con el botón que genera el PDF); el
 * doble clic lo resuelve el plan `2026-04-20-1500-estandarizacion-botones-descarga`.
 */
export function BudgetDownloadButton({ budget }) {
  const { notification } = App.useApp();

  const dowloadPdf = (record) => {
    const v = Number(record.version);
    let btn;
    if (v >= 3) btn = <BudgetDowloadV3 budget={record} />;
    else if (v === 2) btn = <BudgetDowloadV2 budget={record} />;
    else btn = <BudgetDowload budget={record} />;
    notification.open({
      message: 'Descargar Presupuesto',
      btn,
    });
  };

  return (
    <Tooltip title="Descargar presupuesto">
      <Button
        size="small"
        icon={<DownloadOutlined />}
        aria-label="Descargar presupuesto"
        onClick={() => dowloadPdf(budget)}
      />
    </Tooltip>
  );
}

/**
 * Acciones del menú "…" que modifican el presupuesto: editar, nueva versión /
 * re-cotizar y eliminar. Qué ítems se muestran lo decide `buildRowActions`; este
 * hook solo sabe ejecutarlos.
 */
export function useBudgetMenuActions({ budget, refreshData, editBudget }) {
  const { document_id } = budget;
  const navigate = useNavigate();
  const { notification, message } = App.useApp();

  // Re-cotización: crea una versión nueva (copia) y abre el editor sobre ella,
  // para que el usuario ajuste precio/alcance. Sin abrir el editor no tendría
  // sentido (quedaría una copia idéntica).
  const reversion = async () => {
    const result = await createBudgetVersionApi(budget.idBudget);
    if (result?.codeMessage) {
      message.error(result.message || 'No se pudo crear la nueva versión');
      return;
    }
    message.success(`Versión v${result.revision} creada — edítala`);
    refreshData?.(true);
    // Abrir el editor sobre la versión nueva (mismos campos copiados, nuevo id).
    editBudget?.({
      ...budget,
      idBudget: result.idBudget,
      revision: result.revision,
      status: 'esperando_aprobar',
      stage: 'esperando_aprobar',
      expired: false,
      superseded: false,
      document_id: null,
      // Vigencia fresca (now + default) que el backend ya seteó en la versión
      // nueva; se muestra en el editor y se preserva si no se toca.
      valid_until: result.valid_until ?? null,
    });
  };

  const toggleActivated = async (record) => {
    const result = await deactivateBudgetApi(getAccessTokenApi(), record.idBudget);

    if (notificationApi(result)) {
      refreshData?.(true);
    }
  };

  const remove = () => {
    notification.open({
      message: `¿Seguro que desea desactivar el presupuesto ${budget.idBudget}?`,
      btn: (
        <Button danger type="primary" onClick={() => toggleActivated(budget)}>
          Desactivar
        </Button>
      ),
    });
  };

  const edit = () => {
    if (document_id) {
      return navigate(paths.admin.presupuestos.documentos(budget.idBudget));
    }
    return editBudget?.(budget);
  };

  return { edit, reversion, remove };
}

export default BudgetDownloadButton;
